import React, {useState, useEffect, useMemo} from 'react';
import {cloneDeep} from "lodash";
import {validateEmail, validateNotEmpty} from "../helpers/Validation";
import {format} from "date-fns";
import {pageValueIsSet, renderPageValue} from "../helpers/Page";
import {promoSectionIsEnabled} from "../helpers/Promo";

const DEFAULT_CONTACT = {
    name: "",
    email: "",
    number_of_tracks: "",
    uk_based: "",
    details: "",
    promo_rate_per_master: "",
}

const TEMP_MAIL_ADDRESS = 'info@conahtucker.co.uk';

const UK_OPTIONS = [
    {value: 'yes', label: 'Yes'},
    {value: 'no', label: 'No'},
]

const RATE_OPTIONS = [
    {value: '40', label: '£40'},
    {value: '60', label: '£60'},
    {value: '80', label: '£80'},
    {value: '100', label: '£100'},
    {value: '120', label: '£120'},
    {value: '140', label: '£140'},
];

const ContactSection = (props) => {
    const {
        page
    } = props;

    const [data, setData]               = useState(DEFAULT_CONTACT);
    const [submitting, setSubmitting]   = useState(false);
    const [errors, setErrors]           = useState(null);
    const [message, setMessage]         = useState(null);

    const promoSectionEnabled = useMemo(() => {
        return promoSectionIsEnabled(page)
    }, [page]);

    useEffect(() => {
        if (submitting) {
            submitForm();
        }
    }, [submitting]);

    const updateField = (field, value) => {
        let clone = cloneDeep(data);

        clone[field] = value;

        setData(clone);
    }

    const validateForm = () => {
        let errors = {};

        if (!validateNotEmpty(data.name)) {
            errors.name = "The name provided is not valid, please submit a valid name.";
        }

        if (!validateEmail(data.email)) {
            errors.email = "The email provided is not valid, please submit a valid email address.";
        }

        if (!validateNotEmpty(data.number_of_tracks)) {
            errors.number_of_tracks = "The number of tracks provided is not valid, please select a value.";
        }

        if (!validateNotEmpty(data.uk_based)) {
            errors.uk_based = "The 'UK based' provided is not valid, please select a value.";
        }

        if (promoSectionEnabled && !validateNotEmpty(data.promo_rate_per_master)) {
            errors.promo_rate_per_master = "The rate per master provided is not valid, please select a suitable value.";
        }

        if (!validateNotEmpty(data.details)) {
            errors.details = "Please provide details on how you want to collaborate.";
        }

        return errors;
    }

    const submitForm = () => {
        // Clear any existing errors
        setErrors(null);
        setMessage(null);

        // Validate the data provided
        let errors = validateForm();
        if (Object.keys(errors).length > 0) {
            // Validation error -> return errors
            setErrors(errors);
            setMessage('Invalid data provided, please see the contact form for details.')
            setSubmitting(false);
            return;
        }

        // Open the email dialog
        openEmailDialog();

        setMessage('Email dialog open, press submit to send.')
        setSubmitting(false);
    }

    const openEmailDialog = () => {
        let date        = format(new Date(), "dd/MM/yyyy");
        let subject     = 'Contact Request: '+date;
        let body        = "Date: "+date+"\r\n "+
            "Name: "+data.name+"\r\n "+
            "Email: "+data.email+"\r\n "+
            "Number of tracks: "+data.number_of_tracks+"\r\n "+
            "UK Based: "+data.uk_based+"\r\n "+
            (promoSectionEnabled ? "Rate per master: "+data.promo_rate_per_master+"\r\n " : '') +
            "Details: "+data.details;

        // Open the default email client
        window.location.href = 'mailto:'+TEMP_MAIL_ADDRESS+ '?subject='+encodeURIComponent(subject)+'&body='+encodeURIComponent(body);
    }

    return (
        <div id="contact-section">
            <div className="section-guttering-container mb-5">
                <div className="section-guttering-inner">
                    <div className="row m-0 p-0">
                        <div className="col-12 col-md-6 m-0 mb-4 p-0 pe-md-5 contact-left-col">
                            <h2 className="m-0 mb-4 p-0 h5 text-theme-black">{renderPageValue(page, 'contact_title', 'Let\'s get you mastered!')}</h2>
                            <p className="m-0 p-0 text-theme-black">{renderPageValue(page, 'contact_body', 'Get in contact...')}</p>
                        </div>
                        <div className="col-12 col-md-6 m-0 mb-5 p-0 ps-md-5 contact-right-col">
                            <form onSubmit={(event) => submitForm(event)}>
                                <div className={"mb-3"}>
                                    <label
                                        htmlFor="name"
                                        className={'text-theme-black ms-1 mb-2'}
                                    >
                                        {renderPageValue(page, 'name_field_placeholder', "Name *")}
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control mb-1"
                                        id="name"
                                        name="name"
                                        defaultValue={data.name}
                                        onChange={(event) => updateField("name", event.target.value)}
                                        disabled={submitting}
                                    />
                                    {!!errors && !!errors.name && (
                                        <div className="d-block invalid-feedback m-0 mb-1">
                                            {errors.name}
                                        </div>
                                    )}
                                </div>
                                <div className={"mb-3"}>
                                    <label
                                        htmlFor="email"
                                        className={'text-theme-black ms-1 mb-2'}
                                    >
                                        {renderPageValue(page, 'email_field_placeholder', "Email *")}
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control mb-1"
                                        id="email"
                                        name="email"
                                        defaultValue={data.email}
                                        onChange={(event) => updateField("email", event.target.value)}
                                        disabled={submitting}
                                    />
                                    {!!errors && !!errors.email && (
                                        <div className="d-block invalid-feedback m-0 mb-1">
                                            {errors.email}
                                        </div>
                                    )}
                                </div>
                                <div className={"mb-3"}>
                                    <label
                                        htmlFor="number_of_tracks"
                                        className={'text-theme-black ms-1 mb-2'}
                                    >
                                        {renderPageValue(page, 'number_of_tracks_field_placeholder', "Number of tracks *")}
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control mb-1"
                                        id="number_of_tracks"
                                        name="number_of_tracks"
                                        defaultValue={data.number_of_tracks}
                                        onChange={(event) => updateField("number_of_tracks", event.target.value)}
                                        disabled={submitting}
                                        min={1}
                                        step={1}
                                    />
                                    {!!errors && !!errors.number_of_tracks && (
                                        <div className="d-block invalid-feedback m-0 mb-1">
                                            {errors.number_of_tracks}
                                        </div>
                                    )}
                                </div>
                                <div className={"mb-3"}>
                                    <label
                                        htmlFor="uk_based"
                                        className={'text-theme-black ms-1 mb-2'}
                                    >
                                        {renderPageValue(page, 'uk_based_field_placeholder', "UK Based *")}
                                    </label>
                                    <select
                                        className="form-control mb-1"
                                        id="uk_based"
                                        name="uk_based"
                                        onChange={(event) => updateField("uk_based", event.target.value)}
                                        disabled={submitting}
                                        value={data.uk_based}
                                    >
                                        <option value="" disabled>Select option</option>
                                        {UK_OPTIONS.map((option) => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {!!errors && !!errors.uk_based && (
                                        <div className="d-block invalid-feedback m-0 mb-1">
                                            {errors.uk_based}
                                        </div>
                                    )}
                                </div>
                                {promoSectionEnabled && (
                                    <div className={"mb-3"}>
                                        <label
                                            htmlFor="promo_rate_per_master"
                                            className={'text-theme-black ms-1 mb-2'}
                                        >
                                            {renderPageValue(page, 'promo_rate_per_master_field_placeholder', "Rate per Master (£) *")}
                                        </label>
                                        <select
                                            className="form-control mb-1"
                                            id="promo_rate_per_master"
                                            name="promo_rate_per_master"
                                            onChange={(event) => updateField("promo_rate_per_master", event.target.value)}
                                            disabled={submitting}
                                            value={data.promo_rate_per_master}
                                        >
                                            <option value="" disabled>Select option</option>
                                            {RATE_OPTIONS.map((option) => (
                                                <option
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        {!!errors && !!errors.promo_rate_per_master && (
                                            <div className="d-block invalid-feedback m-0 mb-1">
                                                {errors.promo_rate_per_master}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className={"mb-3"}>
                                    <label
                                        htmlFor="details"
                                        className={'text-theme-black ms-1 mb-2'}
                                    >
                                        {renderPageValue(page, 'message_field_placeholder', "How can we collaborate together? *")}
                                    </label>
                                    <textarea
                                        className={"form-control mb-1"}
                                        id="details"
                                        name="details"
                                        onChange={(event) => updateField("details", event.target.value)}
                                        disabled={submitting}
                                        defaultValue={data.details}
                                    />
                                    {!!errors && !!errors.details && (
                                        <div className="d-block invalid-feedback m-0 mb-1">
                                            {errors.details}
                                        </div>
                                    )}
                                </div>
                                <p className={"mb-4"} style={{fontSize: '12px'}}>{renderPageValue(page, 'contact_description', 'This will open your default mail client. If you do not have this setup, you can mail me @ \''+TEMP_MAIL_ADDRESS+'\'.')}</p>
                                <button
                                    className={"btn btn-primary btn-theme-black py-2 px-4"}
                                    disabled={submitting}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        event.preventDefault();
                                        setSubmitting(true)
                                    }}
                                >
                                    {submitting ? renderPageValue(page, 'submit_button_submitting', "Setting up email...") : renderPageValue(page, 'submit_button_cta', "Email me")}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContactSection;
